import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBanners, createBanner, updateBanner, deleteBanner, resolveMediaUrl } from '../../api';
import { PageHeader, EmptyState, TableSkeleton } from '../../components/ui';
import Modal from '../../components/ui/Modal';
import ImageUploadField from '../../components/ui/ImageUploadField';
import { Plus, Pencil, Trash2, Image, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const THEMES = [
  { v: 'vedic', l: 'Vedic Experts' },
  { v: 'moon', l: 'Moon / Guidance' },
  { v: 'remedy', l: 'Remedies' },
  { v: 'offer', l: 'Offers' },
  { v: 'emergency', l: 'Emergency' },
];

const POSITIONS = [
  { v: 'home_top', l: 'Home Top' },
  { v: 'home_middle', l: 'Home Middle' },
  { v: 'offer', l: 'Offer' },
];

const LINK_TYPES = [
  { v: 'none', l: 'Astrologer list' },
  { v: 'category', l: 'Category' },
  { v: 'astrologer', l: 'Astrologer profile' },
  { v: 'offer', l: 'Offer / Wallet' },
  { v: 'url', l: 'URL / Screen key' },
];

const TOOL_LINKS: { v: string; l: string }[] = [];

const defaultForm = {
  title_en: '',
  title_hi: '',
  subtitle_en: '',
  subtitle_hi: '',
  cta_en: 'Explore',
  cta_hi: 'देखें',
  imageUrl: '',
  theme: 'ai',
  linkType: 'none',
  linkValue: '',
  position: 'home_top',
  order: 0,
  isActive: true,
};

function pickLocale(map: any, lang: 'en' | 'hi') {
  if (!map) return '';
  if (typeof map === 'string') return map;
  if (map instanceof Map) return map.get(lang) || map.get('en') || '';
  return map[lang] || map.en || '';
}

export default function BannersPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(defaultForm);
  const [posFilter, setPosFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: () => getBanners().then((r) => r.data.data.banners),
  });

  const saveMutation = useMutation({
    mutationFn: (d: any) => (editing ? updateBanner(editing._id, d) : createBanner(d)),
    onSuccess: () => {
      toast.success('Banner saved');
      qc.invalidateQueries({ queryKey: ['admin-banners'] });
      closeModal();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBanner(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin-banners'] });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setModal(true);
  };

  const openEdit = (b: any) => {
    setEditing(b);
    setForm({
      title_en: pickLocale(b.title, 'en'),
      title_hi: pickLocale(b.title, 'hi'),
      subtitle_en: pickLocale(b.subtitle, 'en'),
      subtitle_hi: pickLocale(b.subtitle, 'hi'),
      cta_en: pickLocale(b.cta, 'en') || 'Explore',
      cta_hi: pickLocale(b.cta, 'hi') || 'देखें',
      imageUrl: b.imageUrl || '',
      theme: b.theme || 'vedic',
      linkType: b.linkType || 'none',
      linkValue: b.linkValue || '',
      position: b.position || 'home_top',
      order: b.order ?? 0,
      isActive: b.isActive !== false,
    });
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
    setForm(defaultForm);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.imageUrl?.trim()) {
      toast.error('Please upload a banner image');
      return;
    }
    saveMutation.mutate({
      title: { en: form.title_en, hi: form.title_hi },
      subtitle: { en: form.subtitle_en, hi: form.subtitle_hi },
      cta: { en: form.cta_en, hi: form.cta_hi },
      imageUrl: form.imageUrl.trim(),
      theme: form.theme,
      linkType: form.linkType,
      linkValue: form.linkValue,
      position: form.position,
      order: Number(form.order) || 0,
      isActive: form.isActive,
    });
  };

  const banners = (data || []).filter((b: any) => !posFilter || b.position === posFilter);

  return (
    <div className="p-6">
      <PageHeader title="Banners" subtitle="Upload real promo images and control home placements">
        <button onClick={openCreate} className="btn-primary">
          <Plus size={15} /> Add Banner
        </button>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['admin-banners'] })}
          className="btn-secondary btn-sm"
        >
          <RefreshCw size={14} />
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5">
        {[{ v: '', l: 'All Positions' }, ...POSITIONS].map((p) => (
          <button
            key={p.v}
            onClick={() => setPosFilter(p.v)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${
              posFilter === p.v
                ? 'bg-primary-500 text-white border-primary-500'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p.l}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="p-5 card">
          <TableSkeleton rows={4} cols={5} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {banners.length === 0 ? (
            <div className="col-span-3">
              <EmptyState message="No banners found" icon={<Image size={32} />} />
            </div>
          ) : (
            banners.map((b: any) => (
              <div key={b._id} className="card overflow-hidden">
                <div className="aspect-[16/7] bg-gray-100 relative">
                  <img
                    src={resolveMediaUrl(b.imageUrl)}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.opacity = '0.3';
                    }}
                  />
                  <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                    <span className="badge badge-blue capitalize">
                      {b.position?.replace('_', ' ')}
                    </span>
                    {b.theme && <span className="badge badge-gray capitalize">{b.theme}</span>}
                    {!b.isActive && <span className="badge badge-gray">Inactive</span>}
                  </div>
                </div>
                <div className="p-3">
                  <p className="font-semibold text-sm text-gray-900">
                    {pickLocale(b.title, 'en') || 'No title'}
                  </p>
                  {pickLocale(b.subtitle, 'en') && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {pickLocale(b.subtitle, 'en')}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      Order: {b.order} · {pickLocale(b.cta, 'en') || 'Explore'}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEdit(b)}
                        className="p-1.5 rounded hover:bg-gray-100 text-blue-500"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this banner?')) deleteMutation.mutate(b._id);
                        }}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <Modal open={modal} onClose={closeModal} title={editing ? 'Edit Banner' : 'Add Banner'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <ImageUploadField
            label="Banner Image"
            variant="banner"
            required
            value={form.imageUrl}
            onChange={(url) => setForm({ ...form, imageUrl: url })}
            hint="Use a real photo that matches the feature · 1200×520 recommended"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title (EN) *</label>
              <input
                required
                className="input"
                value={form.title_en}
                onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                placeholder="Talk to Vedic Experts"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title (HI)</label>
              <input
                className="input"
                value={form.title_hi}
                onChange={(e) => setForm({ ...form, title_hi: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subtitle (EN)</label>
              <input
                className="input"
                value={form.subtitle_en}
                onChange={(e) => setForm({ ...form, subtitle_en: e.target.value })}
                placeholder="Personalized guidance · 24×7"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subtitle (HI)</label>
              <input
                className="input"
                value={form.subtitle_hi}
                onChange={(e) => setForm({ ...form, subtitle_hi: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">CTA (EN)</label>
              <input
                className="input"
                value={form.cta_en}
                onChange={(e) => setForm({ ...form, cta_en: e.target.value })}
                placeholder="Start Chat"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">CTA (HI)</label>
              <input
                className="input"
                value={form.cta_hi}
                onChange={(e) => setForm({ ...form, cta_hi: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Theme</label>
              <select
                className="input"
                value={form.theme}
                onChange={(e) => setForm({ ...form, theme: e.target.value })}
              >
                {THEMES.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
              <select
                className="input"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
              >
                {POSITIONS.map((p) => (
                  <option key={p.v} value={p.v}>
                    {p.l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Link Type</label>
              <select
                className="input"
                value={form.linkType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    linkType: e.target.value,
                    linkValue: e.target.value === 'tool' ? form.linkValue || 'horoscope' : form.linkValue,
                  })
                }
              >
                {LINK_TYPES.map((l) => (
                  <option key={l.v} value={l.v}>
                    {l.l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
              <input
                type="number"
                className="input"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
              />
            </div>

            {form.linkType === 'tool' ? (
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">App Tool</label>
                <select
                  className="input"
                  value={form.linkValue}
                  onChange={(e) => setForm({ ...form, linkValue: e.target.value })}
                >
                  {TOOL_LINKS.map((l) => (
                    <option key={l.v} value={l.v}>
                      {l.l}
                    </option>
                  ))}
                </select>
              </div>
            ) : form.linkType !== 'none' ? (
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Link Value</label>
                <input
                  className="input"
                  value={form.linkValue}
                  onChange={(e) => setForm({ ...form, linkValue: e.target.value })}
                  placeholder={
                    form.linkType === 'category'
                      ? 'e.g. marriage, love, career'
                      : form.linkType === 'astrologer'
                        ? 'Astrologer ID'
                        : form.linkType === 'url'
                          ? 'live · or https://…'
                          : 'Value'
                  }
                />
              </div>
            ) : null}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Active on app
          </label>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 justify-center">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="btn-primary flex-1 justify-center"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save Banner'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
